// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getStoredSession } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import type { ContentClient } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import { fillTemplate } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import type { Locale } from '@accenturecodefoundry/mfe-pot-shared-i18n';
import type {
  EiReportingPeriod,
  EiReportingStatus,
  EmploymentInsuranceApiClient,
} from 'employment-insurance-data-access';
import { REPORTING_OVERVIEW_CONTENT_KEYS, type ReportingOverviewContentKey } from './content-client';
import { formatCurrency, formatPeriodRange, formatShortDate } from './format-date';
import { GuidedReport } from './guided-report/GuidedReport';
import { usePageContents } from './use-page-contents';
import './ReportingOverview.css';

export interface ReportingOverviewProps {
  apiClient: EmploymentInsuranceApiClient;
  contentClient: ContentClient;
  locale: Locale;
  /** Called after the demo end-claim succeeds; App returns to apply mode. */
  onClaimEnded: () => void;
}

const FAQ_KEYS = [
  'employment-insurance.reporting-overview.faq1',
  'employment-insurance.reporting-overview.faq2',
  'employment-insurance.reporting-overview.faq3',
  'employment-insurance.reporting-overview.faq4',
  'employment-insurance.reporting-overview.faq5',
  'employment-insurance.reporting-overview.faq6',
] as const satisfies readonly ReportingOverviewContentKey[];

/** Focuses an scds-button's inner <button>: the host has a shadow root without focus delegation. */
function focusScdsButton(host: HTMLElement | null): void {
  const inner = host?.shadowRoot?.querySelector('button');
  (inner ?? host)?.focus();
}

/**
 * Report mode's EI Reporting screen (docs/specs/016-ei-reporting-flow.md),
 * replacing spec 015's stacked Claims + Reporting + ReportingStatus:
 * heading and subtitle, a banner for the earliest reportable unsubmitted
 * period with Review, the Your reports table, six Common questions, a
 * display-only help card, and the demo end-claim control.
 *
 * The schedule comes from the BFF (`periods[]` on reporting-status, see the
 * BFF's computePeriods), so the due logic has one source of truth. Review
 * opens the guided report (docs/specs/018-ei-guided-reporting.md); coming
 * back from its confirmation re-reads the schedule, so the row flips to
 * Submitted with its payment and the banner goes away -- no reload.
 *
 * Icons: the prototype's lucide ClipboardCheck / HeartHandshake have no
 * SCDS equivalent, so the nearest SCDS icons (`document`, `message`) stand
 * in. The banner's card title is an h3 because scds-card's `card-title-tag`
 * allows h3-h6 only.
 *
 * Deviation from spec 018 (sections 2, 5 and 6), by product decision: the
 * guided report is shown unconditionally. The spec put it behind an Unleash
 * flag, `ei-guided-reporting`, default off, with spec 016's minimal confirm
 * step behind Review when off; there is no flag, and that step is removed.
 *
 * Visual guidance: the Figma prototype's ReportingScreen.tsx overview,
 * mapped to SCDS components (scds-card, scds-table) and tokens
 * (ReportingOverview.css). Rows are not interactive (spec: correcting a past
 * report is out of scope), unlike the prototype's clickable due row.
 */
export function ReportingOverview({ apiClient, contentClient, locale, onClaimEnded }: ReportingOverviewProps) {
  const { content } = usePageContents(contentClient, REPORTING_OVERVIEW_CONTENT_KEYS, locale);
  const [status, setStatus] = useState<EiReportingStatus | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [guided, setGuided] = useState<EiReportingPeriod | null>(null);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState(false);
  // Where focus goes once the overview re-renders: the Review button after
  // backing out of the guided report, the Your reports heading after a
  // submit (the row that just changed; the banner and its Review are gone).
  const [pendingFocus, setPendingFocus] = useState<'review' | 'reports' | null>(null);
  const reviewRef = useRef<HTMLElement>(null);
  const reportsHeadingRef = useRef<HTMLHeadingElement>(null);
  const dateLocale = locale === 'fr' ? 'fr' : 'en';

  function label(key: ReportingOverviewContentKey): string {
    return content[key]?.title ?? key;
  }

  const loadStatus = useCallback(async (): Promise<void> => {
    const session = getStoredSession();
    if (!session) {
      return;
    }
    try {
      const found = await apiClient.getReportingStatus(session.sub);
      setStatus(found);
      setLoadError(found === null);
    } catch (err) {
      console.error('Failed to load EI reporting status', err);
      setLoadError(true);
    }
  }, [apiClient]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (guided || !pendingFocus) {
      return;
    }
    if (pendingFocus === 'review') {
      focusScdsButton(reviewRef.current);
    } else {
      reportsHeadingRef.current?.focus();
    }
    setPendingFocus(null);
  }, [guided, pendingFocus, status]);

  async function endClaim(): Promise<void> {
    setEnding(true);
    setEndError(false);
    try {
      await apiClient.endClaim();
      onClaimEnded();
    } catch (err) {
      console.error('Failed to end the EI claim', err);
      setEndError(true);
      setEnding(false);
    }
  }

  if (guided) {
    const index = (status?.periods ?? []).findIndex((period) => period.periodStart === guided.periodStart);
    return (
      <GuidedReport
        apiClient={apiClient}
        contentClient={contentClient}
        locale={locale}
        period={guided}
        nextPeriod={index >= 0 ? status?.periods?.[index + 1] : undefined}
        onExit={(submitted) => {
          void loadStatus().then(() => {
            setGuided(null);
            setPendingFocus(submitted ? 'reports' : 'review');
          });
        }}
      />
    );
  }

  const periods = status?.periods ?? [];
  const bannerPeriod = periods.find((period) => period.status === 'due' || period.status === 'overdue');

  function statusLabel(period: EiReportingPeriod): string {
    switch (period.status) {
      case 'submitted':
        return label('employment-insurance.reporting-overview.statusSubmitted');
      case 'due':
        return fillTemplate(label('employment-insurance.reporting-overview.statusDue'), {
          date: formatShortDate(period.dueDate, dateLocale),
        });
      case 'overdue':
        return label('employment-insurance.reporting-overview.statusOverdue');
      case 'upcoming':
        return label('employment-insurance.reporting-overview.statusUpcoming');
    }
  }

  function paymentLabel(period: EiReportingPeriod): string {
    return period.status === 'submitted' && period.amount !== undefined
      ? fillTemplate(label('employment-insurance.reporting-overview.paymentToBePaid'), {
          amount: formatCurrency(period.amount, dateLocale),
        })
      : label('employment-insurance.reporting-overview.paymentPending');
  }

  return (
    <div className="ei-report">
      <div className="ei-report__intro">
        <scds-section-heading heading={label('employment-insurance.reporting-overview.title')} heading-tag="h1" />
        <p className="ei-report__subtitle">{label('employment-insurance.reporting-overview.subtitle')}</p>
      </div>

      {loadError ? (
        <p role="alert" className="ei-report__error">
          {label('employment-insurance.reporting-overview.loadError')}
        </p>
      ) : null}

      {bannerPeriod ? (
        <scds-card
          className="ei-report__banner"
          layout="horizontal"
          card-title={fillTemplate(label('employment-insurance.reporting-overview.bannerTitle'), {
            period: formatPeriodRange(bannerPeriod.periodStart, bannerPeriod.periodEnd, dateLocale),
          })}
          card-title-tag="h3"
          description={label('employment-insurance.reporting-overview.bannerBody')}
          tone={bannerPeriod.status === 'overdue' ? 'danger' : 'info'}
          tone-label={statusLabel(bannerPeriod)}
        >
          <span slot="scdsCardIcon" aria-hidden="true" className="ei-report__icon-tile">
            <scds-icon name="document" size="md" />
          </span>
          <scds-button
            slot="scdsCardActions"
            ref={reviewRef}
            variant="primary"
            type="button"
            onClick={() => {
              setEndError(false);
              setGuided(bannerPeriod);
            }}
          >
            {label('employment-insurance.reporting-overview.reviewButton')}
            <scds-icon name="arrow-right" size="sm" aria-hidden="true" />
          </scds-button>
        </scds-card>
      ) : null}

      <section className="ei-report__section" aria-labelledby="ei-report-reports-heading">
        <h2 id="ei-report-reports-heading" ref={reportsHeadingRef} tabIndex={-1} className="ei-report__eyebrow">
          {label('employment-insurance.reporting-overview.reportsHeading')}
        </h2>
        {/* scds-table supplies the shell; ReportingOverview.css restyles it to
            the prototype's design (navy header band, colour-coded status
            text rather than pills, bold right-aligned payment, no shadow). */}
        <scds-table className="ei-report__table">
          <table aria-labelledby="ei-report-reports-heading">
            <thead>
              <tr>
                <th scope="col">{label('employment-insurance.reporting-overview.columnPeriod')}</th>
                <th scope="col">{label('employment-insurance.reporting-overview.columnStatus')}</th>
                <th scope="col" className="ei-report__payment">
                  {label('employment-insurance.reporting-overview.columnPayment')}
                </th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period) => (
                <tr key={period.periodStart} data-status={period.status}>
                  <td className="ei-report__period">{formatPeriodRange(period.periodStart, period.periodEnd, dateLocale)}</td>
                  <td>
                    <span className="ei-report__status">{statusLabel(period)}</span>
                  </td>
                  <td className="ei-report__payment">{paymentLabel(period)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </scds-table>
      </section>

      {/* Native <details>/<summary>, styled with SCDS tokens: scds-core has
          no accordion component (logged as a gap in ReportingOverview.css),
          and the native element is keyboard- and screen-reader-operable
          without any script. */}
      <section className="ei-report__section" aria-labelledby="ei-report-faq-heading">
        <h2 id="ei-report-faq-heading" className="ei-report__eyebrow">
          {label('employment-insurance.reporting-overview.faqHeading')}
        </h2>
        <div className="ei-report__faq">
          {FAQ_KEYS.map((key) => (
            <details key={key} className="ei-report__faq-item">
              <summary className="ei-report__faq-question">{label(key)}</summary>
              <p className="ei-report__faq-answer">{content[key]?.body ?? ''}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Display only: no destination exists for the POC (spec). */}
      <section className="ei-report__section" aria-labelledby="ei-report-help-heading">
        <h2 id="ei-report-help-heading" className="ei-report__eyebrow">
          {label('employment-insurance.reporting-overview.helpHeading')}
        </h2>
        <scds-card
          layout="horizontal"
          card-title={label('employment-insurance.reporting-overview.helpTitle')}
          card-title-tag="h3"
          description={label('employment-insurance.reporting-overview.helpBody')}
        >
          <span slot="scdsCardIcon" aria-hidden="true" className="ei-report__icon-tile ei-report__icon-tile--solid">
            <scds-icon name="message" size="md" />
          </span>
        </scds-card>
      </section>

      {/* Demo-only: always visible and labelled, not config-gated (spec) --
          the whole POC is a demo, and this lets the scenario be replayed. */}
      <section className="ei-report__demo" aria-labelledby="ei-report-demo-heading">
        <h2 id="ei-report-demo-heading" className="ei-report__eyebrow">
          {label('employment-insurance.reporting-overview.demoHeading')}
        </h2>
        {endError ? (
          <p role="alert" className="ei-report__error">
            {label('employment-insurance.reporting-overview.endClaimError')}
          </p>
        ) : null}
        <scds-button
          variant="secondary"
          type="button"
          disabled={ending}
          onClick={() => {
            if (!ending) void endClaim();
          }}
        >
          {label('employment-insurance.reporting-overview.demoEndClaimButton')}
        </scds-button>
      </section>
    </div>
  );
}
